import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useUpdateEffect } from "react-use";
import { ConfirmDialog, EditableHours } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import { deleteTimeRecord } from "~/store/actions/actions.timeRecords";
import { timeStringToFloat } from "~/utils/date";
import { translateBackendMessage } from "~/utils/handleErrorMessages";

const TimeValue = ({ id, timeValue, onChange, canEdit }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [time, setTime] = useState(timeValue);
  const [key, setKey] = useState(1);

  useUpdateEffect(() => {
    setTime(timeValue);
  }, [timeValue]);

  const handleSave = useCallback(
    (e) => {
      const value = timeStringToFloat(e.target.value);
      if (value > 0) {
        typeof onChange === "function" && onChange({ value: value });
      } else {
        setDialogOpen(true);
      }
    },
    [onChange]
  );

  const onCancel = useCallback(() => {
    setDialogOpen(false);
    setTime(timeValue);
    setKey((prev) => prev + 1);
  }, [timeValue]);

  const moveToTrash = useCallback(() => {
    dispatch(deleteTimeRecord(id)).then((action) => {
      if (action?.error?.message) {
        showErrorToast(translateBackendMessage(action.error.message));
      } else {
        showSuccessToast(t("Time record moved to trash"));
      }
    });
  }, [dispatch, id, t]);

  return (
    <>
      <EditableHours
        value={time}
        disabled={!canEdit}
        onSave={handleSave}
        withLeadingZero={false}
        allowEmptyValue
        key={key}
      />

      <ConfirmDialog
        open={dialogOpen}
        dialogTitle={t("Move to trash")}
        dialogContent={t(`Move time record to trash?`)}
        confirmBtnText={t("Move")}
        onConfirm={moveToTrash}
        onCancel={onCancel}
      />
    </>
  );
};

TimeValue.propTypes = {
  id: PropTypes.number.isRequired,
  timeValue: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool,
};

TimeValue.defaultProps = {
  onChange: () => {},
  canEdit: false,
};

export default TimeValue;
